import React, { useState, useEffect, useMemo } from 'react';
import { TableRow, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Eye, Trash2, Search } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import WorkBehaviorStatusDropdown from '@/components/admin/WorkBehaviorStatusDropdown';
import InlineBehavioralChart from '@/components/recruitment/InlineBehavioralChart';
import CandidateSelectModal from '@/components/workbehavior/CandidateSelectModal';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';
import { usePersonalityProfile } from '@/hooks/usePersonalityProfile';
import { BehavioralScores } from '@/types/resultsTypes';

interface WorkBehaviorTableRowProps {
  submission: any;
  onViewDetails: (submission: any) => void;
  onSubmissionDeleted: (id: string) => void;
  onStatusUpdate?: (submissionId: string, newStatus: string) => Promise<void>;
  hideProfile?: boolean;
  selectedIdealProfile?: any;
  cognitivePercentile?: number | null;
  isExpanded?: boolean;
  showCompany?: boolean;
  companyName?: string;
  showChart?: boolean;
}

const WorkBehaviorTableRow: React.FC<WorkBehaviorTableRowProps> = ({
  submission,
  onViewDetails,
  onSubmissionDeleted,
  onStatusUpdate,
  hideProfile = false,
  selectedIdealProfile,
  cognitivePercentile,
  isExpanded = true,
  showCompany = false,
  companyName,
  showChart = false
}) => {
  const { toast } = useToast();
  const [selfAssessmentData, setSelfAssessmentData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch self-assessment data and existing comparison session
  useEffect(() => {
    const fetchData = async () => {
      if (!submission.email) return;
      
      setLoading(true);
      try {
        // Fetch self-assessment data
        const { data: selfData, error: selfError } = await supabase
          .from('survey_submissions')
          .select('*')
          .eq('email', submission.email)
          .eq('assessment_type', 'behavioral')
          .order('completed_at', { ascending: true }) // Get the OLDEST assessment to match useDualAssessmentData
          .limit(1);

        if (selfError) {
          console.error('Error fetching self assessment:', selfError);
        } else if (selfData && selfData.length > 0) {
          setSelfAssessmentData(selfData[0]);
        }

        // Fetch existing comparison session
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: comparisonData, error: comparisonError } = await supabase
            .from('comparison_sessions')
            .select(`
              *,
              person2_submission:survey_submissions!comparison_sessions_person2_submission_id_fkey(
                id,
                full_name,
                email,
                scores,
                completed_at
              )
            `)
            .eq('person1_submission_id', submission.id)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1);

          if (comparisonError) {
            console.error('Error fetching comparison session:', comparisonError);
          } else if (comparisonData && comparisonData.length > 0) {
            const session = comparisonData[0];
            if (session.person2_submission) {
              setSelectedCandidate(session.person2_submission);
            }
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [submission.email, submission.id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatScore = (score: number | null | undefined) => {
    if (score === null || score === undefined || isNaN(score)) return 'N/A';
    return Math.round(score);
  };

  const calculateFlags = () => {
    console.log('=== FLAG CALCULATION START ===');
    console.log('Submission email:', submission.email);
    console.log('Self assessment data available:', !!selfAssessmentData);
    console.log('Selected candidate data available:', !!selectedCandidate);
    console.log('Submission scores structure:', submission.scores ? Object.keys(submission.scores) : 'None');
    
    let flagCount = 0;
    const flagDetails: string[] = [];
    
    if (!submission.scores) {
      console.log('ERROR: No submission scores available');
      return { count: flagCount, details: flagDetails };
    }

    try {
      // Enhanced dual assessment detection
      const isDualAssessment = submission.scores.isDualAssessment || 
                              (submission.scores.selfBehavior && submission.scores.workBehavior);
      
      console.log('Assessment type:', isDualAssessment ? 'Dual' : 'Standalone');
      console.log('Flag calculation context:', {
        isDualAssessment,
        hasSubmissionScores: !!submission.scores,
        hasSelfAssessmentData: !!selfAssessmentData,
        hasSelectedCandidate: !!selectedCandidate,
        submissionId: submission.id,
        submissionEmail: submission.email
      });
      
      let selfScores, workScores, selfWordCount, workWordCount;
      
      if (isDualAssessment) {
        // Dual assessment - both behaviors in same submission
        console.log('Processing dual assessment with enhanced error handling');
        try {
          selfScores = calculateBehavioralScores(submission.scores.selfBehavior);
          workScores = calculateBehavioralScores(submission.scores.workBehavior);
          
          // Enhanced word count extraction with multiple property fallbacks
          const selfBehavior = submission.scores.selfBehavior;
          const workBehavior = submission.scores.workBehavior;
          
          selfWordCount = selfBehavior?.selectedWords?.length || 
                         selfBehavior?.selected_words?.length || 0;
          workWordCount = workBehavior?.selectedWords?.length || 
                         workBehavior?.selected_words?.length || 0;
          
          console.log('Dual assessment results:', { 
            selfScores: selfScores ? 'calculated' : 'failed',
            workScores: workScores ? 'calculated' : 'failed',
            selfWordCount, 
            workWordCount 
          });
        } catch (error) {
          console.error('Error processing dual assessment:', error);
          return { count: 0, details: ['Error calculating scores'] };
        }
      } else {
        // Standalone assessments - need both records
        console.log('Processing standalone work assessment');
        
        // Use selected candidate data if available, otherwise use self assessment data
        const comparisonData = selectedCandidate || selfAssessmentData;
        
        if (!comparisonData?.scores) {
          console.log('ERROR: No comparison data available for standalone comparison');
          return { count: 0, details: ['No comparison data available'] };
        }
        
        console.log('Using comparison data from:', selectedCandidate ? 'selected candidate' : 'self assessment');
        
        try {
          // Calculate scores with consistent methodology
          selfScores = calculateBehavioralScores(comparisonData.scores);
          workScores = calculateBehavioralScores(submission.scores);
          
          // Enhanced word count extraction with multiple property fallbacks
          selfWordCount = comparisonData.selected_words?.length || 
                         comparisonData.selectedWords?.length || 0;
          workWordCount = submission.selected_words?.length || 
                         submission.selectedWords?.length || 0;
          
          console.log('Standalone assessment results:', { 
            selfScores: selfScores ? 'calculated' : 'failed',
            workScores: workScores ? 'calculated' : 'failed',
            selfWordCount, 
            workWordCount,
            comparisonSource: selectedCandidate ? 'candidate' : 'self'
          });
        } catch (error) {
          console.error('Error processing standalone assessments:', error);
          return { count: 0, details: ['Error calculating scores'] };
        }
      }
      
      // Validate we have complete data
      if (!selfScores || !workScores) {
        console.log('ERROR: Missing required scores for comparison');
        return { count: 0, details: ['Missing required scores for comparison'] };
      }
      
      console.log('Final scores for comparison:');
      console.log('Self scores:', {
        aPercentile: selfScores.aPercentile,
        bPercentile: selfScores.bPercentile,
        cPercentile: selfScores.cPercentile,
        dPercentile: selfScores.dPercentile
      });
      console.log('Work scores:', {
        aPercentile: workScores.aPercentile,
        bPercentile: workScores.bPercentile,
        cPercentile: workScores.cPercentile,
        dPercentile: workScores.dPercentile
      });
      
      // Enhanced trait crossing analysis with validation
      const traitMapping = {
        'Drive': 'aPercentile',
        'Connection': 'bPercentile', 
        'Task': 'cPercentile',
        'Order': 'dPercentile'
      };
      
      Object.entries(traitMapping).forEach(([traitName, percentileKey]) => {
        const selfPercentile = selfScores[percentileKey];
        const workPercentile = workScores[percentileKey];
        
        // Enhanced validation - ensure numeric values
        if (typeof selfPercentile === 'number' && !isNaN(selfPercentile) && 
            typeof workPercentile === 'number' && !isNaN(workPercentile)) {
          
          const selfAbove50 = selfPercentile > 50;
          const workAbove50 = workPercentile > 50;
          
          // Only flag if one score is clearly above 50 and the other is clearly below 50
          // Scores exactly at 50 are considered neutral and don't trigger flags
          if (selfAbove50 !== workAbove50) {
            const flagDetail = `${traitName}: ${selfPercentile}% → ${workPercentile}%`;
            flagDetails.push(flagDetail);
            console.log(`🚩 FLAG for ${traitName}: self=${selfPercentile}(${selfAbove50 ? 'above' : 'below'}50), work=${workPercentile}(${workAbove50 ? 'above' : 'below'}50)`);
            flagCount++;
          } else {
            console.log(`✓ No flag for ${traitName}: self=${selfPercentile}, work=${workPercentile} (both ${selfAbove50 ? 'above' : 'below'} 50)`);
          }
        } else {
          console.log(`⚠️ Invalid percentile data for ${traitName}: self=${selfPercentile} (${typeof selfPercentile}), work=${workPercentile} (${typeof workPercentile})`);
        }
      });
      
      // Enhanced word count analysis with improved thresholds
      console.log('Word count analysis:', { selfWordCount, workWordCount });
      if (selfWordCount > 0 && workWordCount > 0) {
        const percentChange = Math.abs(((workWordCount - selfWordCount) / selfWordCount) * 100);
        // Use 50% threshold for significant changes to reduce noise
        if (percentChange > 50) {
          const flagDetail = `Word count: ${selfWordCount} → ${workWordCount} (${percentChange.toFixed(1)}% change)`;
          flagDetails.push(flagDetail);
          console.log(`🚩 Word count flag: ${percentChange.toFixed(1)}% change (${selfWordCount} → ${workWordCount})`);
          flagCount++;
        } else {
          console.log(`✓ No word count flag: ${percentChange.toFixed(1)}% change (${selfWordCount} → ${workWordCount})`);
        }
      } else {
        console.log('⚠️ Cannot calculate word count change - insufficient data');
      }
      
      // Enhanced objectivity analysis with model priority
      const objectivityData = {
        model3: workScores.objectivityModel3Percentile,
        model1: workScores.objectivityModel1Percentile,
        model2: workScores.objectivityModel2Percentile,
        ePercentile: workScores.ePercentile
      };
      
      // Priority: Model 3 > Model 1 > Model 2 > ePercentile
      const objectivityPercentile = objectivityData.model3 !== null ? objectivityData.model3 :
                                   (objectivityData.model1 !== null ? objectivityData.model1 :
                                   (objectivityData.model2 !== null ? objectivityData.model2 : objectivityData.ePercentile));
      
      console.log('Style analysis:', {
        ...objectivityData,
        selected: objectivityPercentile
      });
      
      if (typeof objectivityPercentile === 'number' && !isNaN(objectivityPercentile)) {
        // Use 25th percentile threshold for low objectivity
        if (objectivityPercentile < 25) {
          const flagDetail = `Low objectivity: ${objectivityPercentile}% (below 25th percentile)`;
          flagDetails.push(flagDetail);
          console.log(`🚩 Low objectivity flag: ${objectivityPercentile}% (below 25th percentile)`);
          flagCount++;
        } else {
          console.log(`✓ No objectivity flag: ${objectivityPercentile}% (above 25th percentile)`);
        }
      } else {
        console.log('⚠️ No valid objectivity data available');
      }
      
      console.log(`=== FLAG CALCULATION END: ${flagCount} total flags for ${submission.email} ===`);
      
    } catch (error) {
      console.error('CRITICAL ERROR in flag calculation:', error);
      console.log('Error context:', { 
        submissionEmail: submission.email, 
        error: error.message,
        stack: error.stack 
      });
      return { count: 0, details: ['Error in flag calculation'] };
    }
    
    return { count: flagCount, details: flagDetails };
  };

  const flagResult = useMemo(() => calculateFlags(), [submission.scores, selfAssessmentData, selectedCandidate]);

  // Calculate Alignment - how closely work behavior matches natural self (100% = identical)
  const calculateAlignment = () => {
    if (!submission.scores) return null;
    
    try {
      const isDualAssessment = submission.scores.isDualAssessment || 
                              (submission.scores.selfBehavior && submission.scores.workBehavior);
      
      let selfScores, workScores;
      
      if (isDualAssessment) {
        // Dual assessment - both behaviors in same submission
        selfScores = calculateBehavioralScores(submission.scores.selfBehavior);
        workScores = calculateBehavioralScores(submission.scores.workBehavior);
      } else {
        // Standalone assessments - need comparison data
        const comparisonData = selectedCandidate || selfAssessmentData;
        if (!comparisonData?.scores) return null;
        
        selfScores = calculateBehavioralScores(comparisonData.scores);
        workScores = calculateBehavioralScores(submission.scores);
      }
      
      if (!selfScores || !workScores) return null;
      
      // Calculate absolute differences for main traits (using DCTOS names)
      const traits = [
        { self: selfScores.aPercentile, work: workScores.aPercentile, name: 'Drive' },
        { self: selfScores.bPercentile, work: workScores.bPercentile, name: 'Connection' }, 
        { self: selfScores.cPercentile, work: workScores.cPercentile, name: 'Task' },
        { self: selfScores.dPercentile, work: workScores.dPercentile, name: 'Order' }
      ];
      
      const validTraits = traits.filter(trait => 
        typeof trait.self === 'number' && !isNaN(trait.self) && 
        typeof trait.work === 'number' && !isNaN(trait.work)
      );
      
      if (validTraits.length === 0) return null;
      
      // Calculate differences and average
      const differences = validTraits.map(trait => Math.abs(trait.work - trait.self));
      const averageDifference = differences.reduce((sum, diff) => sum + diff, 0) / differences.length;
      
      // Convert to alignment percentage (100% = identical, 0% = maximum difference)
      const alignmentPercentage = Math.round(100 - averageDifference);
      
      return {
        percentage: alignmentPercentage,
        breakdown: validTraits.map(trait => ({
          name: trait.name,
          difference: Math.abs(trait.work - trait.self),
          self: trait.self,
          work: trait.work
        }))
      };
    } catch (error) {
      console.error('Error calculating alignment:', error);
      return null;
    }
  };

  const alignment = useMemo(() => calculateAlignment(), [submission.scores, selfAssessmentData, selectedCandidate]);

  // Calculate self scores for dynamic profile calculation
  const selfScores = useMemo((): BehavioralScores | null => {
    const isDualAssessment = submission.scores?.isDualAssessment || 
      (submission.scores?.selfBehavior && submission.scores?.workBehavior);
    
    if (isDualAssessment) {
      // Dual assessment - use self behavior from submission
      if (submission.scores?.selfBehavior) {
        const calculated = calculateBehavioralScores(submission.scores.selfBehavior);
        return {
          dominance: calculated?.aScore || 0,
          extraversion: calculated?.bScore || 0,
          patience: calculated?.cScore || 0,
          formality: calculated?.dScore || 0,
          objectivity: calculated?.eScore
        };
      }
    } else {
      // Standalone assessment - use comparison data (selected candidate or self assessment)
      const comparisonData = selectedCandidate || selfAssessmentData;
      if (comparisonData?.scores) {
        const calculated = calculateBehavioralScores(comparisonData.scores);
        return {
          dominance: calculated?.aScore || 0,
          extraversion: calculated?.bScore || 0,
          patience: calculated?.cScore || 0,
          formality: calculated?.dScore || 0,
          objectivity: calculated?.eScore
        };
      }
    }
    
    return null;
  }, [submission.scores, selfAssessmentData, selectedCandidate]);

  // Use usePersonalityProfile for dynamic calculation (same as results page)
  const selfProfile = usePersonalityProfile(
    selfScores || { dominance: 0, extraversion: 0, patience: 0, formality: 0 }
  );

  const selfProfileType = selfScores ? selfProfile.type : 'Unknown';

  const getAlignmentColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-950';
    if (percentage >= 60) return 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950';
    if (percentage >= 40) return 'text-yellow-600 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-950';
    return 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950';
  };

  const getAlignmentLabel = (percentage: number) => {
    if (percentage >= 80) return 'Highly Aligned';
    if (percentage >= 60) return 'Well Aligned';
    if (percentage >= 40) return 'Moderately Aligned';
    return 'Low Alignment';
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!onStatusUpdate) return;
    
    try {
      await onStatusUpdate(submission.id, newStatus);
      toast({
        title: "Status Updated",
        description: `Status changed to ${newStatus}`,
      });
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const handleCandidateSelect = async (candidate: any) => {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to save candidate selections",
          variant: "destructive",
        });
        return;
      }

      // Check if a comparison session already exists
      const { data: existingSession, error: fetchError } = await supabase
        .from('comparison_sessions')
        .select('id')
        .eq('person1_submission_id', submission.id)
        .eq('user_id', user.id)
        .limit(1);

      if (fetchError) {
        console.error('Error checking existing session:', fetchError);
        toast({
          title: "Error",
          description: "Failed to save candidate selection",
          variant: "destructive",
        });
        return;
      }

      if (existingSession && existingSession.length > 0) {
        // Update existing session
        const { error: updateError } = await supabase
          .from('comparison_sessions')
          .update({
            person2_submission_id: candidate.id,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSession[0].id);

        if (updateError) {
          console.error('Error updating comparison session:', updateError);
          toast({
            title: "Error",
            description: "Failed to save candidate selection",
            variant: "destructive",
          });
          return;
        }
      } else {
        // Create new session
        const { error: insertError } = await supabase
          .from('comparison_sessions')
          .insert({
            user_id: user.id,
            person1_submission_id: submission.id,
            person2_submission_id: candidate.id,
            comparison_data: {}
          });

        if (insertError) {
          console.error('Error creating comparison session:', insertError);
          toast({
            title: "Error",
            description: "Failed to save candidate selection",
            variant: "destructive",
          });
          return;
        }
      }

      setSelectedCandidate(candidate);
    } catch (error) {
      console.error('Error saving candidate selection:', error);
      toast({
        title: "Error",
        description: "Failed to save candidate selection",
        variant: "destructive",
      });
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleRemove = async () => {
    setIsDeleting(true);
    try {
      // Just call the parent handler - it handles the actual deletion
      await onSubmissionDeleted(submission.id);
    } catch (error) {
      console.error('Error in remove handler:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClearCandidate = async () => {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        toast({
          title: "Authentication Error", 
          description: "You must be logged in to clear candidate selections",
          variant: "destructive",
        });
        return;
      }

      // Delete the comparison session
      const { error } = await supabase
        .from('comparison_sessions')
        .delete()
        .eq('person1_submission_id', submission.id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting comparison session:', error);
        toast({
          title: "Error",
          description: "Failed to clear candidate selection",
          variant: "destructive",
        });
        return;
      }

      setSelectedCandidate(null);
    } catch (error) {
      console.error('Error clearing candidate selection:', error);
      toast({
        title: "Error",
        description: "Failed to clear candidate selection",
        variant: "destructive",
      });
    }
  };


  return (
    <>
      <TableRow className="hover:bg-muted/30 hover:shadow-sm transition-all duration-200 group">
        {/* Name */}
        <TableCell className="px-4 py-3">
          <div className="font-medium text-foreground truncate">{submission.full_name}</div>
          <div className="text-sm text-muted-foreground truncate">{submission.email}</div>
        </TableCell>
        {/* Pattern/Profile Type */}
        <TableCell className="px-4 py-3 text-center">
          <div className="space-y-1">
            <div className="flex items-center justify-center gap-1.5">
              <div className="text-xs font-medium text-foreground">
                {selfProfileType}
              </div>
              {submission.assessment_variant === 'model2' && (
                <Badge 
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 h-4 bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800"
                >
                  M2
                </Badge>
              )}
            </div>
            {!hideProfile && submission.profile_name && (
              <div className="text-xs text-muted-foreground">
                {submission.profile_name}
              </div>
            )}
          </div>
        </TableCell>

        {/* Self Assessment Chart */}
        <TableCell className="px-4 py-3 text-center">
          {(() => {
            const isDualAssessment = submission.scores?.isDualAssessment || 
                                    (submission.scores?.selfBehavior && submission.scores?.workBehavior);
            
            if (isDualAssessment && submission.scores?.selfBehavior) {
              return <InlineBehavioralChart scores={submission.scores.selfBehavior.scores} />;
            } else if (selfAssessmentData?.scores) {
              return <InlineBehavioralChart scores={selfAssessmentData.scores} />;
            } else if (selectedCandidate) {
              return (
                <div className="relative">
                  <InlineBehavioralChart scores={selectedCandidate.scores} />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearCandidate}
                    className="absolute -top-1 -right-1 h-4 w-4 px-0 text-xs"
                  >
                    ×
                  </Button>
                </div>
              );
            } else {
              return (
                <button
                  onClick={handleOpenModal}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer flex items-center justify-center gap-1 p-2 rounded hover:bg-muted/50"
                >
                  <Search className="h-3 w-3" />
                  <span>No data</span>
                </button>
              );
            }
          })()}
        </TableCell>

        {/* Work Behavior Chart */}
        <TableCell className="px-4 py-3 text-center">
          {(() => {
            const isDualAssessment = submission.scores?.isDualAssessment || 
                                    (submission.scores?.selfBehavior && submission.scores?.workBehavior);
            
            const workScores = isDualAssessment && submission.scores?.workBehavior 
              ? submission.scores.workBehavior.scores 
              : submission.scores;
            
            return (
              <InlineBehavioralChart 
                scores={workScores} 
                comparisonScores={selectedCandidate?.scores || selfAssessmentData?.scores}
              />
            );
          })()}
        </TableCell>

        {/* Alignment */}
        <TableCell className="px-4 py-3 text-center">
          {alignment ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-block">
                  <Badge 
                    className={`text-xs cursor-help px-2 py-1 ${getAlignmentColor(alignment.percentage)}`}
                  >
                    {alignment.percentage}%
                  </Badge>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="p-2 z-50">
                <div className="text-xs space-y-1">
                  {alignment.breakdown.map((trait, index) => (
                    <div key={index} className="flex justify-between gap-4">
                      <span>{trait.name}</span>
                      <span className="text-muted-foreground">{trait.self}% → {trait.work}%</span>
                    </div>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="text-xs text-muted-foreground">—</div>
          )}
        </TableCell>

        {/* Comments */}
        <TableCell className="px-4 py-3 text-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-block">
                <Badge 
                  variant="outline"
                  className={`text-xs cursor-help ${
                    flagResult.count === 0 
                      ? 'bg-green-50 text-green-700 border-green-200' 
                      : flagResult.count <= 2 
                        ? 'bg-yellow-50 text-yellow-700 border-yellow-200' 
                        : 'bg-red-50 text-red-700 border-red-200'
                  }`}
                >
                  {flagResult.count}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="p-2 z-50">
              <div className="text-xs">
                {flagResult.count === 0 ? (
                  <span className="text-muted-foreground">No notable shifts</span>
                ) : (
                  <div className="space-y-1">
                    {flagResult.details.map((detail, index) => (
                      <div key={index}>• {detail}</div>
                    ))}
                  </div>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TableCell>

        {/* Cognitive */}
        <TableCell className="px-4 py-3 text-center">
          <div className="text-sm font-medium text-foreground">
            {cognitivePercentile ? formatScore(cognitivePercentile) : 'N/A'}
          </div>
        </TableCell>

        {/* Match percentage (if ideal profile selected) */}
        {selectedIdealProfile && (
          <TableCell className="px-4 py-3 text-center">
            <div className="text-sm font-medium text-foreground">
              {submission.matchPercentage ? `${formatScore(submission.matchPercentage)}%` : 'N/A'}
            </div>
          </TableCell>
        )}

        {/* Actions */}
        <TableCell className="px-4 py-3">
          <div className="flex items-center gap-1 justify-start">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetails(submission)}
              className="h-8 w-8 p-0 hover:bg-muted/30 transition-colors duration-fast"
              title="View details"
            >
              <Eye className="h-4 w-4 text-primary" />
            </Button>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-muted/30 transition-colors duration-fast"
                  disabled={isDeleting}
                  title="Remove from company"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove Candidate</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to remove {submission.full_name} from your company's view? 
                    Their assessment data will be preserved but they will no longer appear in your dashboard.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleRemove}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Removing...' : 'Remove'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      </TableRow>
      
      {/* Candidate Selection Modal */}
      <CandidateSelectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectCandidate={handleCandidateSelect}
      />
    </>
  );
};

export default WorkBehaviorTableRow;